import { Component, ElementRef, Input, OnDestroy, OnInit, AfterViewInit, ViewChild, HostListener, inject, Inject, ChangeDetectionStrategy, ChangeDetectorRef, signal, afterNextRender, Injector } from '@angular/core';
import { filter, take, takeUntil } from 'rxjs/operators';
import { fromEvent, Subject } from 'rxjs';
import { AuthenticationService } from '@finder/shared/services/authentication/authentication.service';
import { timeout } from '@finder/shared/utils/time/timeout';
import { CommonModule } from '@angular/common';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { ElementChangeDirective } from '@finder/shared/directives/element-change/element-change.directive';
import { AriaLabelHtmlDirective } from '@finder/shared/directives/aria-label-html/aria-label-html.directive';
import { ConfigService } from '@finder/core/config.service';
import { DOCUMENT } from '@angular/common';
import { ButtonComponent } from '../button/button.component';

declare let wdprProfile: any;

@Component({
    selector: 'finder-live-person',
    templateUrl: './live-person.component.html',
    styleUrls: ['./live-person.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TooltipComponent,
        LoadingSpinnerComponent,
        ElementChangeDirective,
        AriaLabelHtmlDirective,
        ButtonComponent,
    ],
})
export class LivePersonComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() data: any;
    @ViewChild('container', { read: ElementRef, static: false }) container: ElementRef;
    @ViewChild('livePersonContainer', { static: false }) livePersonContainerRef: ElementRef<HTMLDivElement>;

    showLogin = signal(false);
    loading = true;
    isSubmitClickedWithoutAcceptingTermsError = signal(false);
    isSubmitClickedWithServiceError = signal(false);
    isSubmitClickedWithServiceSuccess = signal(false);
    isCancelClicked = signal(false);
    WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED = 'WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED';
    LP_EMBEDDED_DIV_ID = 'lp_embedded_div';
    syndicatedLayoutConfigs: Record<string, any>;
    loggedIn = signal(false);
    DASModalEnabled = signal(false);

    private destroy$ = new Subject<void>();
    private spinnerSub$;
    private document: Document = inject(DOCUMENT);
    private dasModalObserver: MutationObserver | null = null;
    private injector = inject(Injector);

    constructor(
        private authenticationService: AuthenticationService,
        private lazyLoader: LazyLoaderService,
        private debugLog: DebugLogService,
        private configService: ConfigService,
        private cdRef: ChangeDetectorRef,
    ) {
        this.syndicatedLayoutConfigs = this.configService.getValue('syndicatedLayout');
    }

    ngOnInit() {
        const DASModalScriptUrl = `${this.syndicatedLayoutConfigs.baseUrl}${this.configService.getValue('DASModalScriptUrl')}`;
        // Early return if script URL is not defined
        if (!DASModalScriptUrl) {
            this.debugLog.error('FINDER_LIVE_PERSON_INIT wdprProfileScriptUrl is not defined in the environment');

            return;
        }

        this.debugLog.info('FINDER_LIVE_PERSON_INIT detected wdprProfileScriptUrl with value ' + DASModalScriptUrl);

        this.lazyLoader.lazyLoadScriptResource(DASModalScriptUrl, false)
            .pipe(
                take(1)
            )
            .subscribe({
                complete: () => {
                    // Early return if wdprProfile is not defined
                    if (!wdprProfile) {
                        this.debugLog.error('FINDER_LIVE_PERSON_INIT wdprProfile is not defined');

                        return;
                    }

                    // Load the DAS modal library
                    this.debugLog.info('FINDER_LIVE_PERSON_INIT wdprProfile initializing...');
                    wdprProfile.main.init();
                    this.debugLog.info('FINDER_LIVE_PERSON_INIT wdprProfile initialized successfully!!');
                }
            });

        const clearValues = () => {
            this.showLogin.set(true);
            this.loading = false;
        };

        // If there's an issue getting the status from navigation we should remove the spinner and show the login
        this.spinnerSub$ = timeout(clearValues, 5000);

        // Make sure the button can't be pressed while the chat is closed for the day
        if (!this.data.enabled) {
            // Per GIT-35766 the high-call volume alert should not show when the button is disabled (as the DRC is closed).
            // This is inelegant but works. setTimeout is necessary to allow the subsequent block data to render;
            // without it, the selector can't find anything.
            setTimeout(() => {
                this.document.querySelectorAll('.generic-text-block.accent').forEach((elem) => {
                    (elem as HTMLElement).style.display = 'none';
                });
            }, 250);

            clearValues();
            this.spinnerSub$.unsubscribe();

            return;
        }

        // Now we wait on the event from navigation and we can display the livePerson button
        fromEvent(this.document, 'navigationChat')
            .pipe(
                take(1),
            ).subscribe({
                next: (result: any) => {
                    this.spinnerSub$.unsubscribe();

                    // When Authentication is OFF just return.
                    if (!this.data.authenticationEnabled) {
                        return;
                    }

                    // If authenticated, remove spinner and show button
                    if (result.detail.authenticated) {
                        this.removeSpinner();
                    } else {
                        // Not authenticated: show login, hide spinner
                        this.showLogin.set(true);
                        this.loading = false;
                        this.cdRef.markForCheck();
                    }
                }
            });

        this.authenticationService.isLoggedIn()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: result => {
                    this.loggedIn.set(result ? true : false);
                },
                error: err => {
                    this.loggedIn.set(false);
                },
                complete: () => {}
            });
    }

    ngAfterViewInit() {
        this.setDASModalEnabled();

        if (this.DASModalEnabled()) {
            return;
        }

        // Only set up the observer if the element isn't present yet
        afterNextRender(() => {
            const container = this.document.body;
            if (container) {
                this.dasModalObserver = new MutationObserver(() => {
                    if (!this.dasModalObserver) return;

                    this.setDASModalEnabled();
                    if (this.DASModalEnabled() && this.dasModalObserver) {
                        this.cleanupObserver();
                    }
                });

                this.dasModalObserver.observe(container, {
                    childList: true,
                    subtree: true,
                });

                timeout(() => {
                    if (this.dasModalObserver) {
                        this.debugLog.info('FINDER_LIVE_PERSON - DASModal element not found after 5s, disconnecting observer (old UI assumed)');
                        this.cleanupObserver();
                    }
                }, 5000);
            } else {
                this.debugLog.info('FINDER_LIVE_PERSON - livepersonButtonEnabled container not found');
            }
        }, { injector: this.injector });
    }

    ngOnDestroy() {
        if (this.dasModalObserver) {
            this.dasModalObserver.disconnect();
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * @description Disconnect and nullify the MutationObserver
     *
     * @name        observerDisconnect
     */
    cleanupObserver() {
        this.dasModalObserver.disconnect();
        this.dasModalObserver = null;
    }

    /**
     * @description Remove the loading spinner when the LP button
     *              is available on the page.
     * @name        removeSpinner
     * @param event
     */
    removeSpinner() {
        if (!this.loading) {
            return;
        }

        // Cancel the login timeout
        this.spinnerSub$.unsubscribe();

        this.loading = false;
        this.showLogin.set(false);
        this.container.nativeElement.classList.add('visible');
    }

    // Submit Button Clicked Without Clicking Accept Terms Error Event Handler
    // Break glass if needed
    // @HostListener('window:submitButtonClickedWithoutClickingAcceptTermsErrorEvent', ['$event'])
    // submitButtonClickedWithoutClickingAcceptTermsErrorEventHandler(event: Event) {
    //     this.isSubmitClickedWithoutAcceptingTermsError.set(true);
    //     this.debugLog.info(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> ${event.type}`);
    // }

    // Submit Button Clicked With Service Error Returned Event Handler
    // Break glass if needed
    // @HostListener('window:submitButtonClickedWithServiceErrorReturnedEvent', ['$event'])
    // submitButtonClickedWithServiceErrorReturnedEventHandler(event: Event) {
    //     this.isSubmitClickedWithServiceError.set(true);
    //     this.debugLog.info(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> ${event.type}`);
    // }

    @HostListener('window:submitButtonClickedWithServiceSuccessReturnedEvent', ['$event'])
    submitButtonClickedWithServiceSuccessReturnedEventHandler(event: Event) {
        this.isSubmitClickedWithServiceSuccess.set(true);
        this.debugLog.info(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> ${event.type}`);

        // Get DOM element of first child of #lp_embedded_div
        let lpEmbeddedDivElement = this.document.getElementById(this.LP_EMBEDDED_DIV_ID);

        if (!lpEmbeddedDivElement) {
            this.debugLog.error(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED}
                -> ${event.type} - error retrieving the DOM element #${this.LP_EMBEDDED_DIV_ID}
                - element is null`);

            return;
        }

        this.debugLog.info(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> ${event.type}
            - successfully detected the DOM element #${this.LP_EMBEDDED_DIV_ID}`);

        let lpEmbeddedDivElementChild = lpEmbeddedDivElement.firstChild as HTMLElement | null;

        if (!lpEmbeddedDivElementChild) {
            this.debugLog.info(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> ${event.type}
                - could not detect the DOM element #${this.LP_EMBEDDED_DIV_ID} first child`);

            return;
        }

        this.debugLog.info(`${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> ${event.type}
            - successfully detected the DOM element #${this.LP_EMBEDDED_DIV_ID} first child`);
        lpEmbeddedDivElementChild.click();
    }

    // Cancel Button Clicked Event Handler
    // Break glass if needed
    // @HostListener('window:cancelButtonClickedEvent', ['$event'])
    // cancelButtonClickedEventHandler(event: Event) {
    //     this.isCancelClicked.set(true);
    //     this.debugLog.info(`TEST 1 -> ${this.WDPR_PROFILE_DAS_MODAL_EVENT_DETECTED} -> submitButtonClickedWithoutClickingAcceptTermsErrorEvent`);
    // }

    /**
     * @description If the button is enabled, we redirect the guest to the Disney
     *              Login in order to start the LP conversation. If not, just return.
     * @name        checkLogin
     * @param       event click event.
     */
    checkLogin(event: Event) {
        this.debugLog.info(`FINDER_LIVE_PERSON checkLogin click detected ${event}`);
        this.debugLog.info(`FINDER_LIVE_PERSON checkLogin this.data.enabled = ${this.data.enabled}`);

        if (!this.data.enabled) {
            return;
        }

        // Stop the event from bubbling
        event.preventDefault();
        event.stopPropagation();

        this.authenticationService.validateLogin(true, { returnUrl: this.data.button.url })
            .pipe(
                // If the guest cancels the lightbox we should listen until the page refreshes
                // using "take(1)" will only open the lightbox once
                takeUntil(this.destroy$),
                filter((swid) => swid)
            )
            .subscribe({
                next: () => {
                    this.removeSpinner();
                    if (this.DASModalEnabled()) {
                        wdprProfile.das?.openModalHandler(event);
                        this.debugLog.info(`FINDER_LIVE_PERSON checkLogin wdprProfile modal successfully opened ${event}`);
                    } else {
                        this.debugLog.error(`FINDER_LIVE_PERSON checkLogin DAS Modal is not enabled.
                            this.DASModalEnabled() = ${this.DASModalEnabled()}`);
                    }
                }
            });
    }

    /**
     * @description Check for T&C button pseudo-toggle.
     *
     * @name        setDASModalEnabled
     */
    setDASModalEnabled(): void {
        const exists = this.document.querySelectorAll('#livepersonButtonEnabled finder-multi-cta a');
        this.DASModalEnabled.set(exists.length > 0);
        this.debugLog.info(`FINDER_LIVE_PERSON checkForElement this.DASModalEnabled() = ${this.DASModalEnabled()}`);
    }
}
